import React, { useEffect, useState, useCallback } from 'react';
import { XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useBillingStore } from '../store/billingStore';
import { useBilling } from '../hooks/useBilling';
import { EXPENSE_CATEGORY_LABELS, EXPENSE_CATEGORY_TO_UTBMS } from '../types/billing.types';
import type {
  ExpenseCategory,
  CreateExpenseDto,
  UpdateExpenseDto,
} from '../types/billing.types';
import ClientMatterPicker from '../../../components/shared/ClientMatterPicker';
import type { ClientMatterSelection } from '../../../components/shared/ClientMatterPicker';
import UtbmsCodeSelector from './UtbmsCodeSelector';

const CATEGORIES = Object.entries(EXPENSE_CATEGORY_LABELS) as [ExpenseCategory, string][];

const ExpenseDrawer: React.FC = () => {
  const store = useBillingStore();
  const { createExpense, updateExpense } = useBilling();
  const isEdit = store.drawerMode === 'edit';
  const editExpense = isEdit
    ? store.expenses.find((e) => e.expense_id === store.selectedExpenseId) ?? null
    : null;

  const [matterId, setMatterId] = useState('');
  const [matterLabel, setMatterLabel] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [expenseDate, setExpenseDate] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ExpenseCategory>('other');
  const [vendor, setVendor] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [isTaxable, setIsTaxable] = useState(false);
  const [taxRate, setTaxRate] = useState('');
  const [utbmsExpenseCode, setUtbmsExpenseCode] = useState('');
  const [isNonBillable, setIsNonBillable] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editExpense) {
      setMatterId(editExpense.matter_id);
      // Populate labels from the matter relation if available
      const matterNum = editExpense.matter?.matter_number || '';
      const matterDesc = editExpense.matter?.description || '';
      setMatterLabel(matterNum ? `${matterNum}${matterDesc ? ` — ${matterDesc}` : ''}` : editExpense.matter_id);
      setClientLabel(editExpense.matter?.client?.client_name || '');
      setShowPicker(false);
      setExpenseDate(editExpense.expense_date?.split('T')[0] || '');
      setDescription(editExpense.description);
      setCategory(editExpense.expense_category);
      setVendor(editExpense.vendor || '');
      setUnitCost(String(editExpense.unit_cost));
      setQuantity(String(editExpense.quantity));
      setIsTaxable(editExpense.is_taxable);
      setTaxRate(editExpense.tax_rate != null ? String(editExpense.tax_rate) : '');
      setUtbmsExpenseCode(editExpense.utbms_expense_code || '');
      setIsNonBillable(editExpense.is_non_billable);
      setNotes(editExpense.notes || '');
    } else {
      setMatterId('');
      setMatterLabel('');
      setClientLabel('');
      setShowPicker(false);
      setExpenseDate(new Date().toISOString().split('T')[0]);
      setDescription('');
      setCategory('other');
      setVendor('');
      setUnitCost('');
      setQuantity('1');
      setIsTaxable(false);
      setTaxRate('');
      setUtbmsExpenseCode('');
      setIsNonBillable(false);
      setNotes('');
    }
  }, [editExpense, store.drawerOpen]);

  const handleMatterSelect = useCallback((selection: ClientMatterSelection) => {
    setMatterId(selection.matterId);
    setMatterLabel(
      selection.matterNumber
        ? `${selection.matterNumber}${selection.matterName ? ` — ${selection.matterName}` : ''}`
        : selection.matterName || selection.matterId
    );
    setClientLabel(selection.clientName);
    setShowPicker(false);
  }, []);

  const handlePickerCancel = useCallback(() => {
    setShowPicker(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const cost = parseFloat(unitCost) || 0;
      const qty = parseFloat(quantity) || 1;
      const totalAmount = cost * qty;

      if (isEdit && editExpense) {
        const dto: UpdateExpenseDto = {
          expense_date: expenseDate,
          description,
          expense_category: category,
          vendor: vendor || undefined,
          unit_cost: cost,
          quantity: qty,
          total_amount: totalAmount,
          is_taxable: isTaxable,
          tax_rate: isTaxable && taxRate ? parseFloat(taxRate) : undefined,
          is_non_billable: isNonBillable,
          notes: notes || undefined,
        };
        // Include matter_id only if it changed
        if (matterId && matterId !== editExpense.matter_id) {
          dto.matter_id = matterId;
        }
        await updateExpense(editExpense.expense_id, dto);
      } else {
        const dto: CreateExpenseDto = {
          matter_id: matterId,
          expense_date: expenseDate,
          description,
          expense_category: category,
          utbms_expense_code: utbmsExpenseCode || undefined,
          vendor: vendor || undefined,
          unit_cost: cost,
          quantity: qty,
          total_amount: totalAmount,
          is_taxable: isTaxable,
          tax_rate: isTaxable && taxRate ? parseFloat(taxRate) : undefined,
          is_non_billable: isNonBillable,
          notes: notes || undefined,
        };
        await createExpense(dto);
      }
      store.closeDrawer();
    } catch {
      // error already set in store by hook
    } finally {
      setSubmitting(false);
    }
  };

  const computedTotal =
    unitCost && quantity
      ? (parseFloat(unitCost) * parseFloat(quantity)).toFixed(2)
      : '-';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={store.closeDrawer}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-[420px] bg-white dark:bg-gray-900 shadow-xl z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Expense' : 'New Expense'}
          </h2>
          <button
            onClick={store.closeDrawer}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Client / Matter Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Client / Matter
            </label>
            {matterId && !showPicker ? (
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm">
                <div className="flex-1 min-w-0">
                  {clientLabel && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{clientLabel}</div>
                  )}
                  <div className="text-gray-900 dark:text-white truncate">{matterLabel}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPicker(true)}
                  className="p-1 text-gray-400 hover:text-lex-accent rounded flex-shrink-0"
                  title="Change client/matter"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
              </div>
            ) : showPicker ? (
              <ClientMatterPicker
                onSelect={handleMatterSelect}
                onCancel={handlePickerCancel}
                variant="light"
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className="w-full px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-lex-accent hover:text-lex-accent transition-colors text-left"
              >
                Select client & matter...
              </button>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date
            </label>
            <input
              type="date"
              required
              value={expenseDate}
              onChange={(e) => setExpenseDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>

          {/* Category & UTBMS Expense Code */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => {
                  const cat = e.target.value as ExpenseCategory;
                  setCategory(cat);
                  // Auto-suggest UTBMS expense code from category
                  const suggested = EXPENSE_CATEGORY_TO_UTBMS[cat];
                  if (suggested) setUtbmsExpenseCode(suggested);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              >
                {CATEGORIES.map(([val, label]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <UtbmsCodeSelector
              value={utbmsExpenseCode}
              onChange={setUtbmsExpenseCode}
              codeSet="expense"
              label="UTBMS Expense Code"
              placeholder="e.g. E107"
            />
          </div>

          {/* Vendor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Vendor
            </label>
            <input
              type="text"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="Who was paid"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>

          {/* Unit cost & Quantity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Unit Cost ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity
              </label>
              <input
                type="number"
                step="1"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>

          {/* Computed total */}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Total: <span className="font-medium tabular-nums text-gray-900 dark:text-white">${computedTotal}</span>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              required
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the expense..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Internal Notes
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional internal notes..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none"
            />
          </div>

          {/* Tax */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={isTaxable}
                onChange={(e) => setIsTaxable(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Taxable
            </label>
            {isTaxable && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tax Rate (decimal, e.g. 0.08)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>
            )}
          </div>

          {/* Non-billable toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={isNonBillable}
              onChange={(e) => setIsNonBillable(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Non-billable
          </label>
        </form>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={store.closeDrawer}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit as any}
            disabled={submitting || !matterId || !description || !unitCost}
            className="flex-1 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ExpenseDrawer;
